import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, Clock, User, Building, Navigation, FileSignature, Pencil } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PointageDetailModal = ({ pointage, isOpen, onClose, theme, canEditLocation, onEditLocation }) => {
  if (!pointage) return null;

  const isManual = pointage.source === 'admin_manual';
  const hasCoords =
    pointage.latitude != null &&
    pointage.longitude != null &&
    !Number.isNaN(Number(pointage.latitude)) &&
    !Number.isNaN(Number(pointage.longitude));
  const hasAddress = !!(pointage.location_address && String(pointage.location_address).trim());
  const position = hasCoords ? [Number(pointage.latitude), Number(pointage.longitude)] : [45.75, 4.85];

  const mapsHref = hasCoords
    ? `https://www.google.com/maps?q=${pointage.latitude},${pointage.longitude}`
    : hasAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(pointage.location_address).trim())}`
      : null;

  const tzParis = { timeZone: 'Europe/Paris' };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...tzParis,
      }),
      time: date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        ...tzParis,
      }),
    };
  };

  const dateInfo = formatDate(pointage.timestamp);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0"
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.border,
        }}
      >
        <DialogHeader className="p-4 pb-3 border-b flex-shrink-0" style={{ borderColor: theme.border }}>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-bold flex items-center gap-3" style={{ color: theme.text }}>
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  pointage.type_pointage === 'Arrivee'
                    ? 'bg-green-100 text-green-600'
                    : 'bg-red-100 text-red-600'
                }`}
              >
                <Clock className="h-5 w-5" />
              </div>
              Détails du scan
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isManual ? (
            <div
              className="rounded-xl border px-3 py-2.5 text-sm"
              style={{ backgroundColor: theme.accentSoft, borderColor: theme.border, color: theme.text }}
            >
              <p className="font-semibold text-xs uppercase tracking-wide" style={{ color: theme.accent }}>
                Ajout Manuel Administrateur
              </p>
              {pointage.admin_comment ? (
                <p className="mt-1.5 whitespace-pre-wrap" style={{ color: theme.text }}>
                  {pointage.admin_comment}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Informations principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div
              className="p-3 rounded-xl border"
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4" style={{ color: theme.textSecondary }} />
                <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                  Type
                </span>
              </div>
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                  pointage.type_pointage === 'Arrivee'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {pointage.type_pointage === 'Arrivee' ? '🟢 Arrivée' : '🔴 Départ'}
              </span>
            </div>

            <div
              className="p-3 rounded-xl border"
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <User className="h-4 w-4" style={{ color: theme.textSecondary }} />
                <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                  Employé
                </span>
              </div>
              <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>
                {pointage.prenom} {pointage.nom}
              </p>
            </div>

            <div
              className="p-3 rounded-xl border"
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Building className="h-4 w-4" style={{ color: theme.textSecondary }} />
                <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                  Site
                </span>
              </div>
              <p className="text-sm font-semibold truncate" style={{ color: theme.text }}>
                {pointage.site}
              </p>
            </div>

            <div
              className="p-3 rounded-xl border"
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4" style={{ color: theme.textSecondary }} />
                <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                  Date/Heure
                </span>
              </div>
              <p className="text-sm font-semibold" style={{ color: theme.text }}>
                {dateInfo.time}
              </p>
              <p className="text-xs capitalize truncate" style={{ color: theme.textSecondary }}>
                {dateInfo.date}
              </p>
            </div>
          </div>

          {hasAddress ? (
            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: theme.background, borderColor: theme.border }}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                    Adresse / lieu (renseigné)
                  </p>
                  <p className="text-sm font-medium mt-1 whitespace-pre-wrap" style={{ color: theme.text }}>
                    {pointage.location_address}
                  </p>
                </div>
                {mapsHref ? (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0"
                  >
                    <Navigation className="h-3 w-3" />
                    Maps
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}

          {hasCoords ? (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: theme.border }}>
              <div
                className="p-3 border-b flex items-center justify-between"
                style={{
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm" style={{ color: theme.text }}>
                      Localisation GPS
                    </h3>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {pointage.precision != null
                        ? `Précision: ±${Math.round(pointage.precision)} m`
                        : 'Précision non renseignée'}
                    </p>
                  </div>
                </div>
                {mapsHref ? (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Navigation className="h-3 w-3" />
                    Google Maps
                  </a>
                ) : null}
              </div>

              <div style={{ height: '250px', width: '100%' }}>
                <MapContainer center={position} zoom={16} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={position}>
                    <Popup>
                      <div className="text-center">
                        <strong>
                          {pointage.prenom} {pointage.nom}
                        </strong>
                        <br />
                        {pointage.type_pointage === 'Arrivee' ? 'Arrivée' : 'Départ'}
                        <br />
                        {dateInfo.time}
                      </div>
                    </Popup>
                  </Marker>
                  {pointage.precision ? (
                    <Circle
                      center={position}
                      radius={pointage.precision}
                      pathOptions={{
                        color: '#3b82f6',
                        fillColor: '#3b82f6',
                        fillOpacity: 0.1,
                      }}
                    />
                  ) : null}
                </MapContainer>
              </div>
            </div>
          ) : !hasAddress ? (
            <div
              className="p-4 rounded-xl border text-center"
              style={{
                backgroundColor: theme.background,
                borderColor: theme.border,
              }}
            >
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-30" style={{ color: theme.textSecondary }} />
              <p className="font-medium text-sm" style={{ color: theme.text }}>
                Localisation GPS non disponible
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pointage.signature_url && !isManual ? (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: theme.border }}>
                <div
                  className="p-2 border-b flex items-center gap-2"
                  style={{
                    backgroundColor: theme.background,
                    borderColor: theme.border,
                  }}
                >
                  <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FileSignature className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="font-medium text-sm" style={{ color: theme.text }}>
                    Signature
                  </span>
                </div>
                <div className="p-3 flex justify-center" style={{ backgroundColor: '#fff' }}>
                  <img
                    src={pointage.signature_url}
                    alt="Signature"
                    className="max-h-20 border rounded"
                    style={{ borderColor: theme.border }}
                  />
                </div>
              </div>
            ) : null}

            {pointage.device_id && !isManual ? (
              <div
                className="p-3 rounded-xl border flex items-center"
                style={{
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                }}
              >
                <p className="text-xs font-mono truncate" style={{ color: theme.textSecondary }}>
                  <strong>Appareil:</strong> {String(pointage.device_id).substring(0, 60)}
                  {String(pointage.device_id).length > 60 ? '…' : ''}
                </p>
              </div>
            ) : isManual ? (
              <div
                className="p-3 rounded-xl border text-sm"
                style={{
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                  color: theme.textSecondary,
                }}
              >
                Saisi depuis l’administration (sans signature ni géolocalisation obligatoire).
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="p-3 border-t flex justify-end gap-2 flex-shrink-0 flex-wrap"
          style={{
            backgroundColor: theme.background,
            borderColor: theme.border,
          }}
        >
          {canEditLocation && onEditLocation ? (
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              style={{ borderColor: theme.border, color: theme.text }}
              onClick={() => onEditLocation(pointage)}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Modifier le scan
            </Button>
          ) : null}
          <Button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white rounded-xl px-6">
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PointageDetailModal;
